/**
 * Licensed Materials - Property of IBM
 * IBM Watson Analytics (C) Copyright IBM Corp.  2017, 2020
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 */
define([
	'./FilterDockView',
	'../../widgets/livewidget/nls/StringResources'
], (FilterDockView, StringResources) => {

	return class FilterDockController {

		constructor(options, scope) {
			this.scope = scope;
			this.dashboardApi = options.dashboardApi;
			this.ajaxSvc = this.dashboardApi.getGlassCoreSvc('.Ajax');
			this.logger = this.dashboardApi.getGlassCoreSvc('.Logger');

			this.singleDockMode = options.singleDockMode;
			this.excludeAddToCanvas = options.excludeAddToCanvas;
			this.parentLayoutId = options.parentLayoutId;

			// Note: In order for a child perspective of dashboard-core to overwrite the values of translateable strings
			// there is the StringResources service that is created/registered in dashboard-core. The number is the priority, default is 100
			// We will register dashboard-livewidget at priority 90. Storytelling, for example, is priority 1. No other component
			// should be able to overwrite its strings.
			// We are doing this because the strings for the filter dock reference "tabs" when in Storytelling there are no tabs but
			// rather "scenes" so the strings are overwritten to use a more storytelling correct version using the same key.
			this.stringResources = this.dashboardApi.getDashboardCoreSvc('.StringResources');
			this.stringResources.register(StringResources, 90);

			// Add the drop down menu items to the model.
			options.handlers['menuActionHandler'] = this.menuActionHandler.bind(this);
			options.viewModel['menuItems'] = this._getDefaultMenuItems();
			options.viewModel['menuIcon'] = 'wa-cardpage_menu_hamburger_overflow';

			this.view = this._createView(options);
			this.dashboardApi.on('pagecontext:filterContextUpdated', this.onFilterContextUpdated, this);
			this.dashboardApi.on('pagecontext:shapingModelChanged', this.onShapingModelChanged, this);
		}

		/**
		 *
		 * @returns {Array} Array of actions for the filter overflow menu
		 */
		_getDefaultMenuItems() {
			return [{
				'label': this.stringResources.get('filterClearAllItems'),
				'action': 'filter-menu-clearall-action'
			},{
				'label': this.stringResources.get('filterDeleteAllItems'),
				'action': 'filter-menu-deleteall-action'
			}];
		}

		/**
		 * Handle the filter item menu click action.
		 *
		 * @param {Jquery event} Event containing menu action to handle
		 */
		menuActionHandler(event) {
			const action = event.currentTarget.dataset.action;
			switch (action) {
				case 'filter-menu-deleteall-action':
					return this.dashboardApi.getCanvasWhenReady().then((canvas) => {
						return canvas.getFilters().removeFilters({
							scope: this.scope,
							multi: true
						});
					});
				case 'filter-menu-clearall-action':
					return this.dashboardApi.getCanvasWhenReady().then((canvas) => {
						return canvas.getFilters().clearFilters({
							scope: this.scope,
							multi: true
						});
					});
				default: {
					this.logger.warn('Unknown dock menu action requested: ' + action);
					return Promise.resolve(false);
				}
			}
		}

		/**
		 * Create view
		 *
		 * @return {FilterDockView} filter dock view
		 */
		_createView(options) {
			const view = new FilterDockView(options, {
				scope: this.scope,
				ajaxSvc: this.ajaxSvc,
				label: this._getLabel(this.scope),
				tooltip: this._getTooltip(this.scope),
				dropZoneTooltip: this._getDropZoneTooltip(this.scope),
				overflowMenuAriaLabel: this.stringResources.get('filterOverflowMenu'),
				className: (this.scope === 'global' ? 'global' : 'other'),
				id: (this.scope === 'global' ? 'global' : 'other'),
				parentLayoutId: this.parentLayoutId,
				singleDockMode: this.singleDockMode,
				excludeAddToCanvas: this.excludeAddToCanvas,
				chevron_left: this.stringResources.get('filterDockChevronLeft'),
				chevron_right: this.stringResources.get('filterDockChevronRight')
			});

			view.on('animation-finished', () => {
				this.dashboardApi.triggerDashboardEvent('filterDock:resized');
			});

			return view;
		}

		_getLabel(scope) {
			if (this.singleDockMode) {
				return this.stringResources.get('filterIconLabel_Single');
			} else {
				return (scope === 'global' ? this.stringResources.get('filterIconLabel_Global') : this.stringResources.get('filterIconLabel_Tab'));
			}
		}

		_getTooltip(scope) {
			if (this.singleDockMode) {
				return this.stringResources.get('filterIconTooltip_Single');
			} else {
				return (scope === 'global' ? this.stringResources.get('filterIconTooltip_Global') : this.stringResources.get('filterIconTooltip_Tab'));
			}
		}

		_getDropZoneTooltip(scope) {
			if (this.singleDockMode) {
				return this.stringResources.get('filterDropZoneTooltip_Single');
			} else {
				return (scope === 'global' ? this.stringResources.get('filterDropZoneTooltip_Global') : this.stringResources.get('filterDropZoneTooltip_Tab'));
			}
		}


		/**
		 * Get view
		 *
		 * @return {FilterDockView} view instance
		 */
		getView() {
			return this.view;
		}

		render(isAuthoringMode) {
			this.view.render(isAuthoringMode);
		}

		resize() {
			this.view.trigger('resize');
		}

		destroy() {
			this.view.remove();
			this.view = null;
			if (this.dashboardApi) {
				this.dashboardApi.off('pagecontext:filterContextUpdated', this.onFilterContextUpdated, this);
				this.dashboardApi.off('pagecontext:shapingModelChanged', this.onShapingModelChanged, this);
				this.dashboardApi = null;
				this.onFilterContextUpdatedBindedToThis = null;
			}
		}

		showDock(display) {
			this.view.showDock(display);
		}

		switchDatasource(datasourceId, extraOptions) {
			this.view.switchDatasource(datasourceId, extraOptions);
		}

		changeToConsumeMode() {
			this.view.changeToConsumeMode();
		}

		changeToAuthorMode() {
			this.view.changeToAuthorMode();
		}

		/**
		 * This method is called whenever the pageContext changes.
		 * @param pageContextPayload - 	A standard payload which includes a 'source' and a 'response'.
		 *								The response includes the action 'delete', 'add', 'update' and the items that have been updated.
		 */
		onFilterContextUpdated(pageContextPayload) {
			this._updateFilterItems(pageContextPayload);
		}

		/**
		 * This method is called whenever a shaping model change was handled in the pageContext.
		 * @param pageContextPayload - 	A standard payload which includes a 'source' and a 'response'.
		 *								The response includes the action 'update' and the items that have been updated.
		 */
		onShapingModelChanged(pageContextPayload) {
			this._updateFilterItems(pageContextPayload);
		}

		/**
		 * This method is called whenever the pageContext or the metadata of one or more pageContext items changes.
		 * @param pageContextPayload - 	A standard payload which includes a 'source' and a 'response'.
		 *								The response includes the action 'delete', 'add', 'update' and the items that have been updated.
		 * TODO: After some investigation, it seems that the simplicity of a general update doesn't have enough overhead to warrant the
		 *		logic to determine exactly what items to update.  We should investigate this further though.
		 *		Example of the type of logic that would be needed:
		 *		A value change or deletion of an item in the global context can reduce or re-enable selections for the same item in the tab context.
		 *		(eg.  2004,2005,2006 are initially selected in global filter, 2005, 2006 are initially selected at tab level.
		 *				Change global to remove 2005, then only 2006 should be reported for tab...remove 2005, no selections and count 0 should be reported for tab.)
		 *		Since we know what item(s) changed, We COULD take the item and, if its scope is global, look up the corresponding tab item and re-render it only.
		 *		Simply re-rendering all will automatically pick this up (because the getValueIds will change based on the new globalContext).
		 */
		_updateFilterItems(pageContextPayload) {
			pageContextPayload = pageContextPayload || {};
			if (pageContextPayload.ignorePageContextChanged && pageContextPayload.ignorePageContextChanged()) {
				return;
			}

			this.view.populateFilterItems(pageContextPayload);

			this.view.afi.setCount(this.view.afi.getActiveCount());
			this.view.afi.show(this.view.isCollapsed());

			// Remove item from dock view if it's valueDataItem and is deleted.
			if (pageContextPayload.isDelete()){
				const itemContext = pageContextPayload.getItems();
				if(this._isSetTypeFilter(itemContext)){
					this._removeSetTypeFilterItems(pageContextPayload);
				}else{
					this.view.removeStaleFilterItemsFromMap(pageContextPayload);
				}
			}

		}

		_removeSetTypeFilterItems(pageContextPayload){
			const dockKey = pageContextPayload.getItemExpressionID();
			if (!dockKey) {
				return;
			}
			const filterDockItem = this.view.getFilterItem(dockKey);
			if (filterDockItem) {
				const payload = {
					dockKey: dockKey
				};
				if(filterDockItem.getIsModeledFilter()){
					payload.item = filterDockItem;
				}
				this.view.removeFilterItem(payload);
			}
		}

		/***
		 * A 'set type filter' is either a named set or a model filter - it has no values only an item name/expression
		 */
		_isSetTypeFilter(itemContext) {
			if(itemContext && itemContext.length === 1){
				const _itemContext = itemContext[0];
				return  _itemContext.getIsModeledFilter() || _itemContext.isValueDataItem();
			}
			return false;
		}

		changeScope(newScope) {
			if (this.view && this.scope !== newScope) {
				this.scope = newScope;
				this.view.switchTabScope(newScope);
			}
		}

		isExpanded() {
			return this.view.isExpanded();
		}

		isCollapsed() {
			return this.view.isCollapsed();
		}

		toggleCollapse() {
			return this.view.toggleCollapse();
		}
		expandDock() {
			return this.view.expandDock();
		}

		isSizedByDrag() {
			return this.view.isSizedByDrag();
		}

		resetDockWidth() {
			this.view.resetDockWidth();
		}
	};
});
